import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { DateInput } from "@/components/ui/date-input";
import { fetchTransfers as fetchTransfersApi, getTransfer } from "@/lib/transfersApi";
import { computeItemBaseAmount } from "@/lib/financialCalculations";
import { PriceWithAdjustment } from "@/components/proposals/PriceWithAdjustment";

interface TransfersStepProps {
  data: any;
  onChange: (data: any) => void;
  readOnly?: boolean;
}

const TransfersStep = ({ data, onChange, readOnly }: TransfersStepProps) => {
  const isReadOnly = !!readOnly;
  const [transfers, setTransfers] = useState<any[]>([]);
  const [proposalTransfers, setProposalTransfers] = useState<any[]>([]);
  const didInit = useRef(false);

  useEffect(() => {
    loadTransfers();
  }, []);

  useEffect(() => {
    if (didInit.current) return;
    if (!Array.isArray(data.transfers)) return; // wait for load
    const loadedTransfers = data.transfers as any[];
    const sorted = [...loadedTransfers].sort((a, b) => {
      if (!a.transfer_date) return -1;
      if (!b.transfer_date) return 1;
      return a.transfer_date.localeCompare(b.transfer_date);
    });
    setProposalTransfers(sorted);
    didInit.current = true;
  }, []); // Only run once on mount

  const loadTransfers = async () => {
    const response = await fetchTransfersApi({ page: 1, size: 2000, sortField: "title", sortOrder: "asc" });
    setTransfers(response.items || []);
  };

  const addTransfer = () => {
    if (isReadOnly) return;
    setProposalTransfers([{ transfer_date: "", transfer_type: "Arrival", free_of_charge: false, notes: "" }, ...proposalTransfers]);
  };

  const removeTransfer = (index: number) => {
    if (isReadOnly) return;
    const newTransfers = proposalTransfers.filter((_, i) => i !== index);
    setProposalTransfers(newTransfers);
    onChange({ ...data, transfers: newTransfers });
  };

  const updateTransfer = async (index: number, field: string, value: any) => {
    if (isReadOnly) return;
    const newTransfers = [...proposalTransfers];
    newTransfers[index] = { ...newTransfers[index], [field]: value };
    if (field === "transfer_id") {
      newTransfers[index].transfers = undefined;
      setProposalTransfers(newTransfers);
      onChange({ ...data, transfers: newTransfers });

      if (value) {
        try {
          const selected = await getTransfer(Number(value));
          const updated = [...newTransfers];
          updated[index] = {
            ...updated[index],
            transfers: {
              id: selected.id,
              transfer_code: selected.transfer_code,
              emirate: selected.emirate,
              title: selected.title,
              description: selected.description,
              type: selected.type,
              pricing_type: selected.pricing_type,
              price_op: selected.price_op,
              price_ag: selected.price_ag,
              price_cf: selected.price_cf,
              image_url: selected.image_url,
            },
          };
          setProposalTransfers(updated);
          onChange({ ...data, transfers: updated });
        } catch {
          // Ignore lookup errors and keep manual selection flow.
        }
      }
      return;
    }
    setProposalTransfers(newTransfers);
    onChange({ ...data, transfers: newTransfers });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Transfers</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Add transfer services for the trip
          </p>
        </div>
        <Button type="button" size="sm" onClick={addTransfer} disabled={isReadOnly}>
          <Plus className="mr-2 h-4 w-4" />
          Add Transfer
        </Button>
      </div>

      {proposalTransfers.length === 0 ? (
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center ${isReadOnly ? "" : "cursor-pointer transition-colors hover:bg-muted/30"}`}
          onClick={isReadOnly ? undefined : addTransfer}
          onKeyDown={isReadOnly ? undefined : (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              addTransfer();
            }
          }}
          role={isReadOnly ? undefined : "button"}
          tabIndex={isReadOnly ? -1 : 0}
          aria-label={isReadOnly ? undefined : "Add transfer"}
        >
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-full bg-muted p-3">
              <Plus className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">No transfers added yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click "Add Transfer" to get started
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {proposalTransfers.map((transfer, index) => {
            const selectedTransfer = transfers.find((t) => t.id === transfer.transfer_id);
            
            return (
              <div key={index} className="grid gap-5 p-6 border rounded-lg bg-card relative shadow-sm hover:shadow-md transition-shadow">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute top-3 right-3 h-8 w-8 p-0"
                  onClick={() => removeTransfer(index)}
                  disabled={isReadOnly}
                >
                  <X className="h-4 w-4" />
                </Button>

                <div className="grid grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Transfer</Label>
                    <SearchableSelect
                      items={transfers.map((t) => ({
                        id: t.id,
                        code: t.transfer_code,
                        title: t.title || "",
                        description: t.emirate || undefined,
                      }))}
                      value={transfer.transfer_id}
                      onValueChange={(value) => updateTransfer(index, "transfer_id", parseInt(value.toString()))}
                      placeholder="Select transfer"
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Transfer Type</Label>
                    <Select
                      value={transfer.transfer_type}
                      onValueChange={(value) => updateTransfer(index, "transfer_type", value)}
                      disabled={isReadOnly}
                    >
                      <SelectTrigger disabled={isReadOnly}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Arrival">Arrival</SelectItem>
                        <SelectItem value="Departure">Departure</SelectItem>
                        <SelectItem value="Point-to-point">Point-to-point</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="border-t pt-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Transfer Date</Label>
                    <DateInput
                      value={transfer.transfer_date}
                      onChange={(value) => updateTransfer(index, "transfer_date", value)}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>

                <div className="border-t pt-5">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={transfer.free_of_charge}
                      onCheckedChange={(checked) => updateTransfer(index, "free_of_charge", checked)}
                      disabled={isReadOnly}
                    />
                    <Label className="text-sm font-normal cursor-pointer">Free of Charge (FOC)</Label>
                  </div>
                </div>

                <div className="border-t pt-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Notes</Label>
                    <Textarea
                      value={transfer.notes || ""}
                      onChange={(e) => updateTransfer(index, "notes", e.target.value)}
                      placeholder="Add any additional notes..."
                      className="min-h-[100px]"
                      disabled={isReadOnly}
                    />
                  </div>
                </div>

                <div className="flex justify-end border-t pt-3">
                  <div className="text-right">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Item total</p>
                    <PriceWithAdjustment
                      baseAmount={transfer.free_of_charge ? 0 : computeItemBaseAmount(transfer, transfer.transfers, data)}
                      proposalData={data}
                      emptyLabel={transfer.free_of_charge ? "Free" : !transfer.transfer_id ? "—" : undefined}
                      size="md"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TransfersStep;
